import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { PopoverEditTextComponent } from '../../../basic/popover/popover-edit-text/popover-edit-text.component';
import { Board } from '../../../core/board/board';
import { BoardHolderService } from '../../../core/board/board.holder.service';
import { BoardService } from '../../../core/board/board.service';
import { CoreItemService } from '../../../core/core-item/core-item.service';
import { SessionTemplate } from '../../../core/session-template/session-template';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { Session } from '../../../core/session/session';
import { SessionService } from '../../../core/session/session.service';
import { Task } from '../../../core/task/task';
import { TaskService } from '../../../core/task/task.service';
import { AppColorBackground } from '../../../shared/color/color';
import { LuxonUtils } from '../../../shared/utils/luxon-utils';
import { SetUtils } from '../../../shared/utils/set-utils';
import { BoardIconTitleComponent } from '../../board/board-icon-title/board-icon-title.component';
import { SessionTemplateCardComponent } from '../../session-template/session-template-card/session-template-card.component';
import { AddMemberBtnComponent } from '../../team-member/add-member-btn/add-member-btn.component';

@Component({
  selector: 'app-scenario-task-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzTableModule,
    NzTabsModule,
    NzTagModule,
    NzCollapseModule,
    NzProgressModule,
    NzSkeletonModule,
    NzPopoverModule,
    NzListModule,

    MatTooltip,

    PopoverEditTextComponent,
    BoardIconTitleComponent,
    SessionTemplateCardComponent,
    AddMemberBtnComponent,
  ],
  templateUrl: './scenario-task-card.component.html',
  styleUrl: './scenario-task-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenarioTaskCardComponent {
  @Input() task?: Task;

  @Input() boardIdList?: string[];

  @Input() canEdit: boolean = true;

  @Input({ required: true }) mode?: 'completionReport' | 'gradingReport' | 'userAssignment';

  isLoading = signal<boolean>(false);

  isEditMembersVisible = false;

  sessionTemplateList = toSignal(this.sessionTemplateService.itemList$(), { initialValue: [] });
  personalBoardList = toSignal(this.boardService.getAllUserList$(), { initialValue: [] });

  onTitleSaveClick(text: string) {
    this.task!.title = text;
    this.taskService.updateTask$(this.task!).pipe(take(1)).subscribe();
  }

  onEditMemberClick() {}

  getSessionTemplate = (id?: string) =>
    computed(() => this.sessionTemplateList().find((sessionTemplate) => sessionTemplate.id === id));

  getTimeRangeString() {
    return LuxonUtils.toTimeRangeString(this.task);
  }

  getCellColorByScore(score?: number) {
    if (!score) {
      return 'white';
    }

    let color = AppColorBackground.red;

    if (score > 3) {
      color = AppColorBackground.green;
    } else if (score === 3) {
      color = AppColorBackground.yellow;
    }

    return color;
  }

  isOverDue() {
    if (!this.task?.endTime) {
      return false;
    }
    if (DateTime.local() > this.task?.endTime) {
      return true;
    }

    return false;
  }

  getTotalPercentage = computed(() => {
    const tableData = this.tableData();

    let totalCount = 0;
    let hasScoreCount = 0;

    for (const item of tableData) {
      for (const sessionTemplate of item.sessionTemplateList) {
        totalCount++;
        if (sessionTemplate.highestSessionScore) {
          hasScoreCount++;
        }
      }
    }

    if (totalCount === 0) {
      return 0;
    }

    return Math.round((hasScoreCount / totalCount) * 100);
  });

  constructor(
    private router: Router,
    private taskService: TaskService,
    private sessionService: SessionService,
    private boardService: BoardService,
    private boardHolderService: BoardHolderService,
    private coreItemService: CoreItemService,
    private sessionTemplateService: SessionTemplateService
  ) {}

  ngOnInit() {}

  myBoard = computed(() => {
    return this.boardHolderService.myPersonalBoard();
  });

  tableData = computed(() => {
    const tempData: ScenarioTaskCardTableData[] = [];

    const boardIdSet = SetUtils.fromArray(this.boardIdList);

    const personalBoardList = this.personalBoardList();

    const sessionTemplateList = this.sessionTemplateList();

    const assignedBoardIdSet = SetUtils.fromArray(this.taskService.getAssignedBoardIdList(this.task!.id));

    if (this.task?.frontendData?.sessionTemplateIdAndSessionIdSet) {
      for (const personalBoard of personalBoardList) {
        if (this.boardIdList && !boardIdSet.has(personalBoard.id)) {
          continue;
        }

        if (!assignedBoardIdSet.has(personalBoard.id)) {
          continue;
        }

        const sessionTemplateDataList: {
          sessionTemplate: SessionTemplate;
          highestSessionScore?: number;
          sessionList: Session[];
        }[] = [];

        for (const item of this.task.frontendData?.sessionTemplateIdAndSessionIdSet) {
          const key = item[0];
          const sessionIdSet = item[1];

          const temp = sessionTemplateList.find((sessionTemplate) => sessionTemplate.id === key);

          if (temp) {
            const sessionList = (
              this.coreItemService.getCoreItemListByIdList(Array.from(sessionIdSet)) as Session[]
            ).filter((c) => c.createdById === personalBoard.id);

            let highestSessionScore: number | undefined;

            if (sessionList.length > 0) {
              highestSessionScore = sessionList?.reduce((prev, curr) => {
                const score = curr.subData.score ?? 0;
                return score > prev ? score : prev;
              }, 0);
            }

            sessionTemplateDataList.push({ sessionTemplate: temp, highestSessionScore, sessionList });
          }
        }

        tempData.push({
          member: personalBoard,
          sessionTemplateList: sessionTemplateDataList,
        });
      }
    }

    return tempData;
  });

  unfinishedSessionTemplate(cardTableData: ScenarioTaskCardTableData) {
    return cardTableData.sessionTemplateList.filter((c) => !c.highestSessionScore);
  }

  finishedSessionTemplate(cardTableData: ScenarioTaskCardTableData) {
    return cardTableData.sessionTemplateList.filter((c) => c.highestSessionScore);
  }

  onAddMemberClick(board: Board) {
    this.taskService.assignTaskToBoardList$(this.task!.id, [board.id]).pipe(take(1)).subscribe();
  }

  onDeleteMemberClick(item: ScenarioTaskCardTableData) {
    this.taskService.unassignBoardIdListFromTask$(this.task!.id, [item.member.id]).pipe(take(1)).subscribe();
  }

  onCallClick(templateId: string) {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.sessionService
      .addSession$(templateId)
      .pipe(take(1))
      .subscribe((data) => {
        if (!data) {
          return;
        }
        this.sessionService
          .launchSession$(data)
          .pipe(take(1))
          .subscribe(() => {
            this.isLoading.set(false);
            this.router.navigate(['', 'practice', data.id]);
          });
      });
  }
}

export interface ScenarioTaskCardTableData {
  member: Board;
  sessionTemplateList: {
    sessionTemplate: SessionTemplate;
    highestSessionScore?: number;
    sessionList: Session[];
  }[];
}
